/**
 * Client-generated identifiers, on every browser this app has to run in.
 *
 * `crypto.randomUUID()` exists ONLY in a secure context. `https://` and `http://localhost` have one;
 * `http://192.168.1.20:4100` does not, and that is exactly the URL somebody opens when they test the
 * app on a phone against a laptop dev server. There, `crypto.randomUUID` is `undefined`, and a screen
 * that calls it during render throws `TypeError` before it paints anything.
 *
 * The fallback is `crypto.getRandomValues` (available in insecure contexts) formatted as a v4 UUID,
 * and only if THAT is missing does it fall back to `Math.random`. The weak path is fine for what this
 * value is: an idempotency key is a collision-avoidance token, not a secret, and it is scoped to one
 * participant's request. Nothing here is ever used for key material.
 */

const HEX = '0123456789abcdef';

export function newIdempotencyKey(): string {
  const native = globalThis.crypto?.randomUUID;
  if (typeof native === 'function') return native.call(globalThis.crypto);

  const bytes = new Uint8Array(16);
  if (globalThis.crypto?.getRandomValues) {
    globalThis.crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }

  // Version 4, variant 1, so the value is a well-formed UUID and the backend's parser accepts it.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (b) => HEX[b >> 4] + HEX[b & 15]).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
